/**
 * SimulationSetupPage.tsx — Redesign v2
 * Layout de 3 painéis side-by-side:
 *   P1: Lista de cidades
 *   P2: Escolas da cidade selecionada (multi-select)
 *   P3: Detalhe da escola com mapa + KPIs + avatares por perfil
 * Mobile (<1024px): P2 e P3 abrem como drawer sobre P1
 */
import React, { useEffect, useState, useCallback, Component } from 'react';
import { useSimulationStore } from '../stores/useSimulationStore';
import { useAuthStore } from '../../../store/authStore';
import { SovereignSplash } from '../../../components/security/SovereignSplash';
import { DesktopWindowManager } from '../components/DesktopWindowManager';
import { SimulationState } from '../types';
import { simulationApi } from '../services/simulationApi';
import type { DemoSchool } from '../types';
import { AvatarScrollStrip } from '../components/sim-setup/AvatarScrollStrip';
import { AddSchoolModal } from '../components/sim-setup/AddSchoolModal';
import { SchoolDetailPanelNew } from '../components/sim-setup/SchoolDetailPanelNew';
import { OverviewPanel } from '../components/sim-setup/OverviewPanel';
import { UnifiedMapView } from '../components/sim-setup/UnifiedMapView';
import type { MapDataPoint } from '../components/sim-setup/UnifiedMapView';

// Error boundary para o Leaflet — evita branco total se o mapa crashar
class MapErrorBoundary extends Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: any) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(e: Error) { console.warn('[MapErrorBoundary]', e.message); }
  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div className="flex items-center justify-center bg-white/5 rounded-xl border border-white/10 w-full" style={{ height: 200 }}>
          <span className="text-[10px] text-[#a6adc8]/50">Mapa indisponível</span>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── helpers ────────────────────────────────────────────────────────────────

const TIPO_REDE_LABELS: Record<string, string> = {
  ESTADUAL:   'Estadual',
  MUNICIPAL:  'Municipal',
  PARTICULAR: 'Particular',
  FEDERAL:    'Federal',
};

const TIPO_REDE_COLORS: Record<string, string> = {
  ESTADUAL:   '#89b4fa',
  MUNICIPAL:  '#a6e3a1',
  PARTICULAR: '#fab387',
  FEDERAL:    '#f9e2af',
};

function KpiChip({ icon, value, label }: { icon: string; value: number | string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg bg-white/5 border border-white/8 min-w-[60px]">
      <span className="text-base">{icon}</span>
      <span className="text-sm font-bold text-white">{value}</span>
      <span className="text-[8px] text-[#a6adc8] uppercase tracking-wider">{label}</span>
    </div>
  );
}

// ─── Painel 1: Lista de cidades ──────────────────────────────────────────────

interface CityPreset { id: string; nome: string; uf: string; codeIBGE: string; }

interface CityListPanelProps {
  cities: CityPreset[];
  schools: DemoSchool[];
  selectedCityId: string | null;
  onSelectCity: (city: CityPreset) => void;
  onAddCity: () => void;
  onResetSeed: () => void;
  seedActive: boolean;
  theme: 'dark' | 'light';
}

function CityListPanel({ cities, schools, selectedCityId, onSelectCity, onAddCity, onResetSeed, seedActive, theme }: CityListPanelProps) {
  const schoolsByCity = useCallback((cityName: string) =>
    schools.filter(s => s.cidade.toLowerCase() === cityName.toLowerCase()),
  [schools]);

  const isLight = theme === 'light';

  return (
    <div className="flex flex-col h-full">
      <div className={`px-4 pt-5 pb-3 border-b ${isLight ? 'border-slate-200' : 'border-white/8'}`}>
        <h2 className={`text-xs font-semibold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-[#a6adc8]'}`}>Cidades</h2>
        <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Selecione para configurar</p>
      </div>

      <div className="flex-1 overflow-y-auto py-2 px-2 flex flex-col gap-1.5">
        {cities.map(city => {
          const citySchools = schoolsByCity(city.nome);
          const isSelected = selectedCityId === city.id;
          const isConfigured = citySchools.length > 0;

          return (
            <button
              key={city.id}
              onClick={() => onSelectCity(city)}
              className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all cursor-pointer group ${
                isSelected
                  ? isLight
                    ? 'bg-violet-50 border-violet-300 text-violet-700 shadow-sm'
                    : 'bg-[#4c1d95]/25 border-[#7c3aed]/60 shadow-[0_0_12px_rgba(124,58,237,0.2)]'
                  : isLight
                  ? 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-slate-700'
                  : 'bg-white/3 border-white/6 hover:bg-white/6 hover:border-white/15'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold truncate ${isSelected && isLight ? 'text-violet-700' : isLight ? 'text-slate-700' : 'text-white'}`}>{city.nome}</div>
                  <div className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>
                    {city.uf}{city.codeIBGE ? ` · IBGE ${city.codeIBGE}` : ''}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {isConfigured && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${
                      isLight 
                        ? 'bg-emerald-50 text-emerald-600 border-emerald-200' 
                        : 'bg-[#a6e3a1]/15 text-[#a6e3a1] border-[#a6e3a1]/30'
                    }`}>
                      {citySchools.length} escolas
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}

        <button
          onClick={onAddCity}
          className={`w-full mt-1 py-2.5 rounded-xl border border-dashed text-center text-xs font-bold transition-all cursor-pointer ${
            isLight
              ? 'border-slate-200 hover:border-violet-300 hover:bg-violet-50 text-violet-600'
              : 'border-white/15 hover:border-[#cba6f7]/40 hover:bg-[#cba6f7]/5 text-[#cba6f7]'
          }`}
        >
          + Nova Cidade
        </button>
      </div>

      <div className={`px-3 py-2.5 border-t ${isLight ? 'border-slate-200' : 'border-white/8'}`}>
        <button
          onClick={onResetSeed}
          disabled={seedActive}
          className={`w-full text-xs transition-colors cursor-pointer bg-transparent border-none disabled:opacity-40 font-medium ${
            isLight ? 'text-slate-500 hover:text-violet-600' : 'text-[#a6adc8] hover:text-[#cba6f7]'
          }`}
        >
          {seedActive ? '⏳ Gerando seed...' : '🔄 Resetar Mega Seed'}
        </button>
      </div>
    </div>
  );
}

// ─── Painel 2: Escolas da cidade ─────────────────────────────────────────────

interface SchoolConfigPanelProps {
  city: CityPreset;
  schools: DemoSchool[];
  selectedSchoolIds: Set<string>;
  onToggleSchool: (school: DemoSchool) => void;
  onSelectDetail: (school: DemoSchool | null) => void;
  detailSchoolId: string | null;
  onStartSimulation: () => void;
  starting: boolean;
  onBack: () => void;
  onAddSchool: () => void;
  theme: 'dark' | 'light';
}

function SchoolConfigPanel({
  city, schools, selectedSchoolIds, onToggleSchool, onSelectDetail,
  detailSchoolId, onStartSimulation, starting, onBack, onAddSchool, theme
}: SchoolConfigPanelProps) {
  const citySchools = schools.filter(s => s.cidade.toLowerCase() === city.nome.toLowerCase());
  const totalAlunos = citySchools.filter(s => selectedSchoolIds.has(s.id)).reduce((a, s) => a + s.totalAlunos, 0);
  const totalProf   = citySchools.filter(s => selectedSchoolIds.has(s.id)).reduce((a, s) => a + s.totalProfessores, 0);
  const isLight = theme === 'light';

  const lightRedeColors: Record<string, string> = {
    ESTADUAL:   '#2563eb', // Blue-600
    MUNICIPAL:  '#059669', // Emerald-600
    PARTICULAR: '#d97706', // Amber-600
    FEDERAL:    '#ca8a04', // Yellow-600
  };

  return (
    <div className="flex flex-col h-full">
      <div className={`px-4 pt-5 pb-3 border-b ${isLight ? 'border-slate-200' : 'border-white/8'}`}>
        <div className="flex items-center gap-2">
          <button onClick={onBack} className={`transition-colors cursor-pointer text-sm font-semibold bg-transparent border-none ${isLight ? 'text-slate-500 hover:text-slate-800' : 'text-[#a6adc8] hover:text-white'}`}>←</button>
          <div>
            <h2 className={`text-xs font-bold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-[#a6adc8]'}`}>{city.nome}</h2>
            <p className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>{citySchools.length} escolas demo</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-2 px-2 flex flex-col gap-2">
        {citySchools.length === 0 ? (
          <div className={`text-center text-xs py-8 ${isLight ? 'text-slate-400' : 'text-[#a6adc8]/50'}`}>
            <div className="text-2xl mb-2">🏫</div>
            Nenhuma escola demo nesta cidade.
            <br />
            <span className="text-[11px] opacity-80">Execute o Mega Seed para criar escolas.</span>
          </div>
        ) : (
          <>
            {/* Visão Geral Consolidada Card */}
            {selectedSchoolIds.size > 0 && (
              <div
                className={`rounded-xl border transition-all cursor-pointer px-3 py-2.5 flex items-center gap-3 ${
                  detailSchoolId === null
                    ? isLight
                      ? 'bg-violet-50 border-violet-300 shadow-sm text-violet-700'
                      : 'bg-[#7c3aed]/15 border-[#7c3aed]/70 shadow-[0_0_12px_rgba(124,58,237,0.2)]'
                    : isLight
                    ? 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                    : 'bg-white/3 border-white/6 hover:border-white/12'
                }`}
                onClick={() => onSelectDetail(null)}
              >
                <div className="text-base flex-shrink-0">🗺️</div>
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-bold leading-tight ${isLight && detailSchoolId === null ? 'text-violet-700' : isLight ? 'text-slate-800' : 'text-white'}`}>Visão Geral Consolidada</div>
                  <div className={`text-[10px] mt-0.5 leading-none ${isLight ? 'text-slate-400' : 'text-[#a6adc8]'}`}>Mapa consolidado e Atores</div>
                </div>
              </div>
            )}

            {citySchools.map(school => {
              const isSelected = selectedSchoolIds.has(school.id);
              const isDetail   = detailSchoolId === school.id;
              const color = isLight 
                ? (lightRedeColors[school.tipoRede] ?? '#7c3aed') 
                : (TIPO_REDE_COLORS[school.tipoRede] ?? '#cba6f7');

              return (
                <div
                  key={school.id}
                  className={`rounded-xl border transition-all ${
                    isDetail
                      ? isLight
                        ? 'bg-violet-50/70 border-violet-400 shadow-md'
                        : 'bg-[#4c1d95]/20 border-[#7c3aed]/70 shadow-[0_0_16px_rgba(124,58,237,0.25)]'
                      : isSelected
                      ? isLight
                        ? 'bg-white border-slate-300'
                        : 'bg-white/5 border-white/15'
                      : isLight
                      ? 'bg-slate-50 border-slate-200 opacity-60'
                      : 'bg-white/2 border-white/6 opacity-60'
                  }`}
                >
                  {/* Header da escola */}
                  <div
                    className="flex items-start gap-2.5 px-3 pt-3 pb-2 cursor-pointer"
                    onClick={() => onSelectDetail(school)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={e => { e.stopPropagation(); onToggleSchool(school); }}
                      className={`mt-0.5 w-3.5 h-3.5 flex-shrink-0 cursor-pointer ${isLight ? 'accent-violet-600' : 'accent-[#cba6f7]'}`}
                      onClick={e => e.stopPropagation()}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-sm font-semibold leading-tight truncate ${isLight ? 'text-slate-800' : 'text-white'}`}>{school.nome}</span>
                        <span
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                          style={{ background: color + '12', color }}
                        >
                          {TIPO_REDE_LABELS[school.tipoRede] ?? school.tipoRede}
                        </span>
                      </div>
                      <div className={`text-[11px] mt-0.5 ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>{school.codigo}</div>
                    </div>
                  </div>

                  {/* KPIs mini */}
                  <div className={`flex gap-3 px-3 pb-3 text-[11px] ${isLight ? 'text-slate-500' : 'text-[#a6adc8]'}`}>
                    <span>👨‍🎓 {school.totalAlunos}</span>
                    <span>📚 {school.totalTurmas} turmas</span>
                    <span>👨‍🏫 {school.totalProfessores}</span>
                    {school.latitude && <span className="text-[#a6e3a1]">📍</span>}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Sumário + botão iniciar */}
      <div className={`px-3 py-3 border-t flex flex-col gap-2 ${isLight ? 'border-slate-200' : 'border-white/8'}`}>
        {selectedSchoolIds.size > 0 && (
          <div className={`text-xs flex gap-3 justify-center ${isLight ? 'text-slate-500' : 'text-[#a6adc8]'}`}>
            <span>{selectedSchoolIds.size} escola(s)</span>
            <span>·</span>
            <span>{totalAlunos} alunos</span>
            <span>·</span>
            <span>{totalProf} prof</span>
          </div>
        )}
        <div className="flex gap-2">
          <button
            onClick={onAddSchool}
            className={`flex-shrink-0 py-2.5 px-3 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
              isLight
                ? 'border-violet-300 text-violet-600 hover:bg-violet-50'
                : 'border-[#cba6f7]/30 text-[#cba6f7] hover:bg-[#cba6f7]/10'
            }`}
            title="Criar nova escola demo nesta cidade"
          >
            + Escola
          </button>
          <button
            onClick={onStartSimulation}
            disabled={selectedSchoolIds.size === 0 || starting}
            className={`flex-1 py-2.5 rounded-xl disabled:opacity-40 text-white text-xs font-bold transition-all cursor-pointer disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
              isLight
                ? 'bg-violet-600 hover:bg-violet-700 shadow-sm'
                : 'bg-[#4c1d95] hover:bg-[#5b21b6]'
            }`}
          >
            {starting ? (
              <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Iniciando...</>
            ) : (
              <>▶ Iniciar Simulação</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Painel 3: Detalhe da escola ─────────────────────────────────────────────

function SchoolDetailPanel({ school, theme }: { school: DemoSchool; theme: 'dark' | 'light' }) {
  const [mapHeight, setMapHeight] = useState(() => {
    const saved = localStorage.getItem('ac_school_map_height');
    return saved ? parseInt(saved, 10) : 280;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [detailData, setDetailData] = useState<any | null>(null);
  const isLight = theme === 'light';

  // Reseta dados carregados da escola anterior ao mudar a seleção
  useEffect(() => {
    setDetailData(null);
  }, [school.id]);

  // Coords para pinos sintéticos dos alunos no mapa
  const studentPins: { lat: number; lng: number }[] = [];
  if (school.latitude && school.longitude) {
    const seed = school.totalAlunos;
    for (let i = 0; i < seed; i++) {
      const angle = (i / seed) * 2 * Math.PI;
      const r = 0.003 + Math.sin(i * 17) * 0.009;
      studentPins.push({
        lat: school.latitude  + Math.cos(angle) * r + Math.sin(i * 7)  * 0.004,
        lng: school.longitude + Math.sin(angle) * r + Math.cos(i * 13) * 0.004,
      });
    }
  }

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const mapWrapper = document.getElementById('school-map-wrapper');
      if (!mapWrapper) return;
      const rect = mapWrapper.getBoundingClientRect();
      const newHeight = Math.max(120, Math.min(600, e.clientY - rect.top));
      setMapHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      localStorage.setItem('ac_school_map_height', String(mapHeight));
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, mapHeight]);

  const color = isLight 
    ? (school.tipoRede === 'MUNICIPAL' ? '#059669' : school.tipoRede === 'ESTADUAL' ? '#2563eb' : '#7c3aed') 
    : (TIPO_REDE_COLORS[school.tipoRede] ?? '#cba6f7');

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className={`px-5 py-4 border-b flex-shrink-0 ${isLight ? 'border-slate-200' : 'border-white/8'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className={`text-base font-bold leading-tight truncate ${isLight ? 'text-slate-800' : 'text-white'}`}>{school.nome}</h2>
            <p className={`text-xs mt-0.5 ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>{school.cidade} — {school.uf}</p>
          </div>
          <span
            className="text-[10px] font-bold px-2 py-1 rounded-full flex-shrink-0"
            style={{
              background: color + '12',
              color,
            }}
          >
            {TIPO_REDE_LABELS[school.tipoRede] ?? school.tipoRede}
          </span>
        </div>
      </div>

      {/* Conteúdo Side-by-Side (Mapa à esquerda, KPIs e Listas à direita) */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-hidden">
        
        {/* Coluna da Esquerda: Mapa */}
        <div className="w-full lg:w-[45%] flex flex-col border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-white/8 p-4 flex-shrink-0 lg:flex-shrink">
          <div className="flex-1 relative flex flex-col min-h-[280px] lg:min-h-0">
            <MapErrorBoundary>
              <UnifiedMapView
                dataPoints={[
                  {
                    id: `school-${school.id}`,
                    type: 'SCHOOL',
                    lat: Number(school.latitude),
                    lng: Number(school.longitude),
                    label: school.nome,
                    sublabel: `${school.cidade}/${school.uf}`,
                  },
                  ...studentPins.map((pin, idx) => {
                    const alunosReais = detailData?.alunos ?? [];
                    const alunoReal = alunosReais[idx];
                    
                    // Se tiver o aluno real, usa o status de tag real. Senão, faz fallback sintético
                    const hasTag = alunoReal ? alunoReal.hasTag : (idx % 3 !== 0);

                    // Determina se o aluno possui app instalado com base na contagem real
                    const totalApps = detailData?.totais?.appInstalado ?? Math.floor(school.totalAlunos * 0.6);
                    const hasApp = idx < totalApps;

                    return {
                      id: alunoReal ? `student-${alunoReal.id}` : `student-${idx}`,
                      type: 'STUDENT_HOME' as const,
                      lat: pin.lat,
                      lng: pin.lng,
                      label: alunoReal ? alunoReal.nome : `Residência Aluno #${idx + 1}`,
                      sublabel: alunoReal ? `Matrícula: ${alunoReal.matricula || 'N/A'}` : undefined,
                      payload: {
                        hasTag,
                        hasApp,
                      }
                    };
                  }),
                ]}
                config={{
                  theme,
                  useClusteringByDefault: true,
                  enableToolbar: true,
                }}
                style={{ height: '100%', width: '100%', minHeight: '280px' }}
              />
            </MapErrorBoundary>
          </div>
        </div>

        {/* Coluna da Direita: KPIs e Conteúdo detalhado selecionado */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          <SchoolDetailPanelNew school={school} theme={theme} onDataLoaded={setDetailData} />
        </div>

      </div>
    </div>
  );
}

// ─── Page principal ───────────────────────────────────────────────────────────

export function SimulationSetupPage() {
  const {
    state, schools,
    loadSchools, selectSchool,
    runDemoSeed, seedActive, seedLogs, seedError,
    startSimulation, stopSimulation,
  } = useSimulationStore();

  const { logout } = useAuthStore();
  const [isBackendOnline] = useState(true);

  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const saved = localStorage.getItem('cockpit_theme');
    return (saved as 'dark' | 'light') || 'light';
  });

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('cockpit_theme', next);
  };

  // Seleção de cidade
  const [cities, setCities]         = useState<CityPreset[]>([]);
  const [selectedCity, setSelectedCity] = useState<CityPreset | null>(null);

  // Multi-seleção de escolas
  const [selectedSchoolIds, setSelectedSchoolIds] = useState<Set<string>>(new Set());

  // Escola em detalhe (P3)
  const [detailSchool, setDetailSchool] = useState<DemoSchool | null>(null);

  // Drawer mobile
  const [drawerOpen, setDrawerOpen] = useState<'schools' | 'detail' | null>(null);

  // Modal de reset e logs
  const [showResetModal, setShowResetModal] = useState(false);
  const [cleanManualSchools, setCleanManualSchools] = useState(false);
  const [seedDone, setSeedDone] = useState(false);

  // Auto-scroll para logs do seed
  const logEndRef = React.useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollTop = logEndRef.current.scrollHeight;
    }
  }, [seedLogs]);

  // Iniciar simulação
  const [starting, setStarting] = useState(false);

  // Modal de adicionar escola
  const [showAddSchoolModal, setShowAddSchoolModal] = useState(false);

  const isSimulationActive = [
    SimulationState.MANUAL_CONTROL,
    SimulationState.RUNNING,
    SimulationState.PAUSED,
  ].includes(state as any);

  useEffect(() => {
    loadSchools();
    simulationApi.getCities().then(setCities).catch(console.warn);
  }, []);

  // Ao selecionar cidade, pré-seleciona todas as escolas da cidade
  const handleSelectCity = useCallback((city: CityPreset) => {
    setSelectedCity(city);
    const citySchools = schools.filter(s => s.cidade.toLowerCase() === city.nome.toLowerCase());
    setSelectedSchoolIds(new Set(citySchools.map(s => s.id)));
    setDetailSchool(null);
    setDrawerOpen('schools');
  }, [schools]);

  // Toggle de escola (multi-select)
  const handleToggleSchool = useCallback((school: DemoSchool) => {
    setSelectedSchoolIds(prev => {
      const next = new Set(prev);
      if (next.has(school.id)) next.delete(school.id);
      else next.add(school.id);
      return next;
    });
  }, []);

  // Detalhe de escola (painel P3)
  const handleSelectDetail = useCallback((school: DemoSchool | null) => {
    setDetailSchool(school);
    setDrawerOpen('detail');
  }, []);

  // Iniciar simulação com primeira escola selecionada
  const handleStartSimulation = useCallback(async () => {
    const schoolToStart = detailSchool ?? schools.find(s => selectedSchoolIds.has(s.id));
    if (!schoolToStart) return;
    setStarting(true);
    try {
      await selectSchool(schoolToStart);
      await startSimulation();
    } finally {
      setStarting(false);
    }
  }, [detailSchool, schools, selectedSchoolIds, selectSchool, startSimulation]);

  // Após criar escola pelo modal: refresh + auto-seleciona
  const handleSchoolCreated = useCallback(async (newSchool: DemoSchool) => {
    setShowAddSchoolModal(false);
    await loadSchools(); // recarrega lista completa
    setSelectedSchoolIds(prev => new Set([...prev, newSchool.id]));
    setDetailSchool(newSchool);
    handleSelectDetail(newSchool);
  }, [loadSchools, handleSelectDetail]);

  // ── Render ──
  if (isSimulationActive) {
    return (
      <div className="w-screen h-screen overflow-hidden text-[#cdd6f4] select-none relative">
        <SovereignSplash skipIntro alignLogoRight splitPct={30} titleWord1="Aluno" titleWord2="Conectado" subtitle="Central de Simulação em Tempo Real" hideBackButton>
          <DesktopWindowManager isBackendOnline={isBackendOnline} stopSimulation={stopSimulation} />
        </SovereignSplash>
      </div>
    );
  }

  return (
    <>
    <div className={`w-screen h-screen overflow-hidden select-none relative ${theme === 'light' ? 'bg-[#f8fafc] text-slate-800' : 'text-[#cdd6f4]'}`}
         style={{ background: theme === 'light' ? 'linear-gradient(135deg,#f8fafc 0%,#f1f5f9 60%,#e2e8f0 100%)' : 'linear-gradient(135deg,#11111b 0%,#1e1e2e 60%,#181825 100%)' }}>

        {/* ── Layout 3 painéis (desktop ≥1024px) ─────────────────────────── */}
        <div className="hidden lg:flex h-full w-full max-h-[calc(100vh-0px)] overflow-hidden pointer-events-auto">

          {/* Botão alternar tema */}
          <button
            onClick={toggleTheme}
            className={`absolute top-4 right-28 z-50 px-3 py-1.5 rounded-lg border text-[10px] font-bold transition-all cursor-pointer ${
              theme === 'light'
                ? 'bg-slate-200 hover:bg-slate-300 border-slate-300 text-slate-700'
                : 'bg-white/10 hover:bg-white/20 border-white/20 text-[#a6adc8]'
            }`}
          >
            {theme === 'light' ? '🌙 Escuro' : '☀️ Claro'}
          </button>

          {/* Botão desconectar */}
          <button
            onClick={logout}
            className={`absolute top-4 right-4 z-50 px-3 py-1.5 rounded-lg border text-[10px] font-bold transition-all cursor-pointer ${
              theme === 'light'
                ? 'bg-red-50 hover:bg-red-100 border-red-200 text-red-600'
                : 'bg-[#f38ba8]/10 border-[#f38ba8]/30 text-[#f38ba8] hover:bg-[#f38ba8]/20'
            }`}
          >
            Desconectar
          </button>

          {/* P1 — Cidades */}
          <div className={`w-[200px] flex-shrink-0 h-full border-r overflow-hidden ${
            theme === 'light'
              ? 'bg-slate-50 border-slate-200'
              : 'bg-[#181825]/90 border-r border-white/6'
          }`}>
            <CityListPanel
              cities={cities}
              schools={schools}
              selectedCityId={selectedCity?.id ?? null}
              onSelectCity={handleSelectCity}
              onAddCity={() => {}}
              onResetSeed={() => { setCleanManualSchools(false); setSeedDone(false); setShowResetModal(true); }}
              seedActive={seedActive}
              theme={theme}
            />
          </div>

          {/* P2 — Escolas */}
          <div className={`w-[260px] flex-shrink-0 h-full border-r overflow-hidden ${
            theme === 'light'
              ? 'bg-white border-slate-200'
              : 'bg-[#1e1e2e]/90 border-white/6'
          }`}>
            {selectedCity ? (
              <SchoolConfigPanel
                city={selectedCity}
                schools={schools}
                selectedSchoolIds={selectedSchoolIds}
                onToggleSchool={handleToggleSchool}
                onSelectDetail={handleSelectDetail}
                detailSchoolId={detailSchool?.id ?? null}
                onStartSimulation={handleStartSimulation}
                starting={starting}
                onBack={() => setSelectedCity(null)}
                onAddSchool={() => setShowAddSchoolModal(true)}
                theme={theme}
              />
            ) : (
              <div className={`flex items-center justify-center h-full text-xs text-center px-6 ${
                theme === 'light' ? 'text-slate-400' : 'text-[#a6adc8]/40'
              }`}>
                <div>
                  <div className="text-3xl mb-3">🏙️</div>
                  Selecione uma cidade à esquerda para ver as escolas disponíveis
                </div>
              </div>
            )}
          </div>

          {/* P3 — Detalhe */}
          <div className={`flex-1 h-full overflow-hidden ${
            theme === 'light'
              ? 'bg-slate-50/50'
              : 'bg-[#1e1e2e]/70'
          }`}>
            {detailSchool ? (
              <SchoolDetailPanel school={detailSchool} theme={theme} />
            ) : selectedSchoolIds.size > 0 ? (
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className={`px-5 pt-5 pb-3 border-b flex-shrink-0 ${
                  theme === 'light' ? 'border-slate-200' : 'border-white/8'
                }`}>
                  <div className="min-w-0">
                    <h2 className={`text-base font-bold leading-tight ${theme === 'light' ? 'text-slate-800' : 'text-white'}`}>Visão Geral da Simulação</h2>
                    <p className={`text-xs mt-0.5 ${theme === 'light' ? 'text-slate-400' : 'text-[#6c7086]'}`}>{selectedCity?.nome} — {selectedCity?.uf}</p>
                  </div>
                </div>
                {/* Overview panel body */}
                <div className="flex-1 overflow-y-auto px-5 pb-5 pt-4">
                  <OverviewPanel schoolIds={Array.from(selectedSchoolIds)} theme={theme} />
                </div>
              </div>
            ) : (
              <div className={`flex items-center justify-center h-full text-xs text-center px-8 ${
                theme === 'light' ? 'text-slate-400' : 'text-[#a6adc8]/40'
              }`}>
                <div>
                  <div className="text-4xl mb-3">🗺️</div>
                  Marque escolas à esquerda e clique em<br />Visão Geral para ver as métricas consolidadas
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Layout mobile (<1024px) — drawer ────────────────────────────── */}
        <div className="flex lg:hidden h-full w-full pointer-events-auto flex-col overflow-hidden">
          {/* Header mobile */}
          <div className={`flex justify-between items-center px-4 py-2 border-b ${theme === 'light' ? 'bg-white border-slate-200' : 'border-white/6'}`}>
            <button
              onClick={toggleTheme}
              className={`text-[10px] font-bold px-2.5 py-1 rounded border bg-transparent cursor-pointer ${
                theme === 'light' ? 'border-slate-300 text-slate-700' : 'border-white/20 text-[#a6adc8]'
              }`}
            >
              {theme === 'light' ? '🌙 Escuro' : '☀️ Claro'}
            </button>
            <button onClick={logout} className="text-[#f38ba8] text-[10px] font-bold bg-transparent border-none cursor-pointer">Desconectar</button>
          </div>

          {/* P1 sempre visível no mobile */}
          <div className={`flex-1 overflow-hidden ${theme === 'light' ? 'bg-slate-50' : 'bg-[#181825]/90'}`}>
            <CityListPanel
              cities={cities}
              schools={schools}
              selectedCityId={selectedCity?.id ?? null}
              onSelectCity={handleSelectCity}
              onAddCity={() => {}}
              onResetSeed={() => { setCleanManualSchools(false); setSeedDone(false); setShowResetModal(true); }}
              seedActive={seedActive}
              theme={theme}
            />
          </div>

          {/* Drawer P2 */}
          {drawerOpen === 'schools' && selectedCity && (
            <div className="fixed inset-0 z-40 flex items-end">
              <div className="absolute inset-0 bg-black/60" onClick={() => setDrawerOpen(null)} />
              <div className={`relative w-full rounded-t-2xl max-h-[80vh] flex flex-col shadow-2xl border-t ${
                theme === 'light' ? 'bg-white border-slate-200' : 'bg-[#1e1e2e] border-white/10'
              }`}>
                <div className={`w-10 h-1 rounded-full mx-auto mt-3 mb-1 flex-shrink-0 ${theme === 'light' ? 'bg-slate-300' : 'bg-white/20'}`} />
                <div className="flex-1 overflow-hidden">
                  <SchoolConfigPanel
                    city={selectedCity}
                    schools={schools}
                    selectedSchoolIds={selectedSchoolIds}
                    onToggleSchool={handleToggleSchool}
                    onSelectDetail={(school) => { handleSelectDetail(school); setDrawerOpen('detail'); }}
                    detailSchoolId={detailSchool?.id ?? null}
                    onStartSimulation={handleStartSimulation}
                    starting={starting}
                    onBack={() => setDrawerOpen(null)}
                    onAddSchool={() => setShowAddSchoolModal(true)}
                    theme={theme}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Drawer P3 */}
          {drawerOpen === 'detail' && detailSchool && (
            <div className="fixed inset-0 z-50 flex items-end">
              <div className="absolute inset-0 bg-black/70" onClick={() => setDrawerOpen('schools')} />
              <div className={`relative w-full rounded-t-2xl max-h-[90vh] flex flex-col shadow-2xl border-t ${
                theme === 'light' ? 'bg-white border-slate-200' : 'bg-[#1e1e2e] border-white/10'
              }`}>
                <div className={`w-10 h-1 rounded-full mx-auto mt-3 mb-1 flex-shrink-0 ${theme === 'light' ? 'bg-slate-300' : 'bg-white/20'}`} />
                <div className="flex-1 overflow-hidden">
                  <SchoolDetailPanel school={detailSchool} theme={theme} />
                </div>
              </div>
            </div>
          )}
        </div>

    </div>

    {/* Modal: Adicionar nova escola demo */}
    {showAddSchoolModal && selectedCity && (
      <AddSchoolModal
        municipioId={selectedCity.id}
        onCreated={handleSchoolCreated}
        onClose={() => setShowAddSchoolModal(false)}
      />
    )}

    {/* ── Modal de Confirmação e Progresso do Seed ── */}
    {showResetModal && (
      <div className="fixed inset-0 z-50 bg-[#11111b]/80 backdrop-blur-sm flex items-center justify-center pointer-events-auto">
        <div className={`border rounded-2xl p-6 max-w-lg w-full mx-4 shadow-2xl relative flex flex-col gap-4 text-left ${
          theme === 'light' ? 'bg-white border-slate-200 text-slate-800' : 'bg-[#1e1e2e] border-white/10 text-white'
        }`}>
          
          {/* Header */}
          <div className="flex items-center gap-3">
            <span className="text-2xl">
              {seedActive ? '🔄' : seedError ? '❌' : seedDone ? '🎉' : '⚠️'}
            </span>
            <h3 className={`text-sm font-bold uppercase tracking-wider ${theme === 'light' ? 'text-slate-800' : 'text-white'}`}>
              {seedActive 
                ? 'Semeando Banco de Dados...' 
                : seedError 
                  ? 'Falha na Execução do Seed' 
                  : seedDone 
                    ? 'Reconstituição Concluída!' 
                    : 'Recriar Base de Testes'}
            </h3>
          </div>

          {/* Descrição contextual */}
          <p className={`text-xs leading-relaxed ${theme === 'light' ? 'text-slate-600' : 'text-[#a6adc8]'}`}>
            {seedActive 
              ? 'Por favor, aguarde enquanto o processo reconstrói as tabelas e recria a base de teste padrão (Belo Horizonte).' 
              : seedError 
                ? 'Ocorreu um erro crítico ao executar as migrações e sementes do banco de dados.'
                : seedDone 
                  ? 'O banco de dados foi atualizado com sucesso com todos os responsáveis, alunos e atores fixos de monitoramento.'
                  : 'Esta ação irá reinicializar e recriar as 4 escolas fixas padrão de Belo Horizonte com seus respectivos alunos, turmas, chaveiros RFID e professores.'}
          </p>

          {/* Checkbox para apagar escolas manuais (só no estado de confirmação inicial) */}
          {!seedActive && !seedError && !seedDone && (
            <label className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
              theme === 'light' ? 'bg-slate-50 border-slate-200 hover:bg-slate-100' : 'bg-white/3 border-white/6 hover:bg-white/5'
            }`}>
              <input
                type="checkbox"
                checked={cleanManualSchools}
                onChange={(e) => setCleanManualSchools(e.target.checked)}
                className="mt-0.5 rounded border-white/20 text-[#7c3aed] focus:ring-[#7c3aed]"
              />
              <div>
                <span className={`text-[10px] font-bold ${theme === 'light' ? 'text-slate-800' : 'text-white'}`}>Limpeza Completa (Remover escolas manuais)</span>
                <p className={`text-[9px] mt-0.5 ${theme === 'light' ? 'text-slate-400' : 'text-[#6c7086]'}`}>
                  Se marcado, também exclui escolas de simulação criadas manualmente. Se desmarcado, apenas as escolas padrão de Belo Horizonte são recriadas, preservando as suas.
                </p>
              </div>
            </label>
          )}

          {/* Barra de progresso animada (mostrada durante o seed) */}
          {seedActive && (
            <div className="w-full">
              <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden relative">
                <div className="absolute top-0 bottom-0 left-0 bg-gradient-to-r from-[#7c3aed] to-[#cba6f7] animate-pulse rounded-full" style={{ width: '100%' }}></div>
              </div>
              <span className={`text-[8px] mt-1 block text-right uppercase tracking-wider ${theme === 'light' ? 'text-slate-400' : 'text-[#6c7086]'}`}>Aguardando terminal...</span>
            </div>
          )}

          {/* Área de Logs (mostrada durante ou após o seed) */}
          {(seedActive || seedLogs.length > 0) && (
            <div className="flex flex-col gap-1.5">
              <span className={`text-[8px] font-bold uppercase tracking-wider ${theme === 'light' ? 'text-slate-500' : 'text-[#a6adc8]'}`}>Mensagens do Terminal</span>
              <textarea
                ref={logEndRef}
                readOnly
                value={seedLogs.join('\n')}
                className={`w-full h-36 border rounded-xl p-3 font-mono text-[10px] leading-normal resize-none focus:outline-none focus:ring-1 focus:ring-[#7c3aed]/50 ${
                  theme === 'light' ? 'bg-slate-50 border-slate-300 text-slate-700' : 'bg-[#11111b] border-white/8 text-[#a6e3a1]'
                }`}
              />
            </div>
          )}

          {/* Ações (Rodapé de botões) */}
          <div className={`flex justify-end gap-2 border-t pt-4 mt-1 ${theme === 'light' ? 'border-slate-200' : 'border-white/6'}`}>
            {!seedActive && !seedError && !seedDone && (
              <>
                <button
                  onClick={() => setShowResetModal(false)}
                  className={`px-4 py-2 rounded-xl border transition-all text-xs font-bold cursor-pointer ${
                    theme === 'light' ? 'bg-slate-50 hover:bg-slate-100 border-slate-300 text-slate-700' : 'bg-white/5 border-white/10 text-[#cdd6f4] hover:bg-white/10'
                  }`}
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    setSeedDone(false);
                    await runDemoSeed(cleanManualSchools);
                    setSeedDone(true);
                  }}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#7c3aed] to-[#9a5ced] text-white hover:from-[#8b4fed] hover:to-[#a76fed] shadow-lg shadow-[#7c3aed]/15 transition-all text-xs font-bold cursor-pointer"
                >
                  Sim, Recriar Base
                </button>
              </>
            )}

            {seedActive && (
              <span className={`text-xs italic py-2 ${theme === 'light' ? 'text-slate-400' : 'text-[#6c7086]'}`}>
                Executando procedimentos no servidor...
              </span>
            )}

            {seedDone && !seedActive && (
              <button
                onClick={() => window.location.reload()}
                className={`px-5 py-2 rounded-xl shadow-lg transition-all text-xs font-bold cursor-pointer ${
                  theme === 'light' ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-emerald-500/15' : 'bg-[#a6e3a1] text-[#11111b] hover:bg-[#a6e3a1]/90 shadow-[#a6e3a1]/15'
                }`}
              >
                🔄 Atualizar Página
              </button>
            )}

            {seedError && !seedActive && (
              <button
                onClick={() => setShowResetModal(false)}
                className={`px-4 py-2 rounded-xl border transition-all text-xs font-bold cursor-pointer ${
                  theme === 'light' ? 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100' : 'bg-[#f38ba8]/10 border-[#f38ba8]/30 text-[#f38ba8] hover:bg-[#f38ba8]/20'
                }`}
              >
                Fechar
              </button>
            )}
          </div>

        </div>
      </div>
    )}
    </>
  );
}
